import { useState, useEffect, useCallback } from 'react';
import {
  Building,
  Users,
  Calendar,
  DollarSign,
  Zap,
  Save,
  Plus,
  Edit2,
  Trash2,
  Loader2,
  CheckCircle2,
  Lock,
  Unlock,
  Archive,
  X,
  Shield,
  AlertCircle,
  Key,
  Globe,
  Brain,
  Sparkles,
  Coins,
} from 'lucide-react';
import clsx from 'clsx';
import { PeriodStatus, PeriodType, RateType, periodDisplayName, type Period, type User, type FxRate, type Currency, type SSOProvider, type MFASetup } from '@/types';
import { authApi, usersApi, periodsApi, fxRatesApi, currenciesApi, ssoApi, mfaApi, aiConfigApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- Tab Type ----------------------------------------------------------------

type SettingsTab = 'general' | 'users' | 'periods' | 'currencies' | 'fx_rates' | 'skills' | 'ai_llm' | 'sso' | 'mfa';

// --- Hardcoded skill configs (metadata is backend-defined) -------------------

const defaultSkillConfigs = [
  { name: 'data_validation', display_name: 'Data Validation', enabled: true, params: { strict_balance_check: true, max_tolerance: 0.01 } },
  { name: 'ic_elimination', display_name: 'IC Elimination', enabled: true, params: { match_threshold: 100, auto_match: true } },
  { name: 'fx_translation', display_name: 'FX Translation', enabled: true, params: { method: 'current_rate', cta_account: '3300' } },
  { name: 'equity_consolidation', display_name: 'Equity Consolidation', enabled: true, params: { goodwill_method: 'full', minority_interest: true } },
  { name: 'generate_statements', display_name: 'Statement Generation', enabled: true, params: { include_notes: false, rounding: 'thousands' } },
];

// --- Role Badge --------------------------------------------------------------

function RoleBadge({ role }: { role: string }) {
  const config: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
    admin: { bg: 'bg-red-50', text: 'text-red-700', icon: Shield },
    accountant: { bg: 'bg-blue-50', text: 'text-blue-700', icon: Building },
    auditor: { bg: 'bg-purple-50', text: 'text-purple-700', icon: Lock },
    viewer: { bg: 'bg-gray-100', text: 'text-gray-600', icon: Users },
  };
  const c = config[role] || config.viewer;
  return (
    <span className={clsx('badge gap-1 capitalize', c.bg, c.text)}>
      {role}
    </span>
  );
}

// --- Period Status Badge -----------------------------------------------------

function PeriodStatusBadge({ status }: { status: PeriodStatus }) {
  const config: Record<PeriodStatus, { label: string; bg: string; text: string; icon: React.ElementType }> = {
    [PeriodStatus.OPEN]: { label: 'Open', bg: 'bg-green-50', text: 'text-green-700', icon: Unlock },
    [PeriodStatus.CLOSED]: { label: 'Closed', bg: 'bg-amber-50', text: 'text-amber-700', icon: Archive },
    [PeriodStatus.LOCKED]: { label: 'Locked', bg: 'bg-gray-100', text: 'text-gray-600', icon: Lock },
  };
  const c = config[status];
  const StatusIcon = c.icon;
  return (
    <span className={clsx('badge gap-1', c.bg, c.text)}>
      <StatusIcon className="w-3 h-3" />
      {c.label}
    </span>
  );
}

// --- Settings Page -----------------------------------------------------------

export default function Settings() {
  const { addNotification } = useAppStore();

  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // General settings state (from user's tenant)
  const [tenantName, setTenantName] = useState('');
  const [reportingCurrency, setReportingCurrency] = useState('USD');
  const [fyEndMonth, setFyEndMonth] = useState(12);
  const [fyEndDay, setFyEndDay] = useState(31);
  const [tenantSlug, setTenantSlug] = useState('');

  // Users state
  const [users, setUsers] = useState<User[]>([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userForm, setUserForm] = useState({ email: '', full_name: '', password: '', role: 'accountant' as User['role'] });
  const [savingUser, setSavingUser] = useState(false);

  // Periods state
  const [periods, setPeriods] = useState<Period[]>([]);
  const [showPeriodModal, setShowPeriodModal] = useState(false);
  const [periodForm, setPeriodForm] = useState({ year: new Date().getFullYear(), month: 12, period_type: PeriodType.ANNUAL as PeriodType, start_date: '', end_date: '' });
  const [savingPeriod, setSavingPeriod] = useState(false);

  // Currencies state
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [currenciesLoading, setCurrenciesLoading] = useState(false);
  const [showAddCurrency, setShowAddCurrency] = useState(false);
  const [newCurrency, setNewCurrency] = useState({ code: '', name: '', symbol: '' });
  const [savingCurrency, setSavingCurrency] = useState(false);

  // FX Rates state
  const [fxRates, setFxRates] = useState<FxRate[]>([]);
  const [fxPeriodFilter, setFxPeriodFilter] = useState('');
  const [showAddFx, setShowAddFx] = useState(false);
  const [newFx, setNewFx] = useState({ from_currency: '', to_currency: 'AED', rate_type: RateType.CLOSING, rate: '' });
  const [savingFx, setSavingFx] = useState(false);
  const [isReverseView, setIsReverseView] = useState(false);

  // Monthly FX rates from spreadsheet (Currency → AED, index 0 = Jan, 11 = Dec)
  // Shape: { currency: [ [avgJan, endJan], [avgFeb, endFeb], ... ] }
  const MONTHLY_RATES: Record<string, [number | null, number | null][]> = {
    AED: [[1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1], [1, 1]],
    GBP: [[4.5797, 4.55831], [4.5931955, 4.628084], [4.688311, 4.748538], [4.820153, 4.891768], [4.915821, 4.939874], [4.982753, 5.025631], [4.9346445, 4.843658], [4.8958095, 4.947961], [4.9422675, 4.936574], [4.901811348, 4.817215], [4.8252863, 4.857798], [4.8983045, 4.938811]],
    AZN: [[2.16029, 2.16029], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294], [2.160294, 2.160294]],
    INR: [[0.04266, 0.04241], [0.042217, 0.04202], [0.0424885, 0.042957], [0.0431865, 0.043416], [0.0431675, 0.042919], [0.042861, 0.042802], [0.042366, 0.04193], [0.041781, 0.041632], [0.041491, 0.04135], [0.04155187, 0.041385], [0.04133635, 0.041073], [0.0409695, 0.040866]],
    Rp: [[0.000226, 0.000225], [0.000225, 0.000222], [0.000223, 0.000222], [0.000219, 0.000221], [0.000224, 0.000225], [0.000226, 0.000226], [0.000225609, 0.000223], [0.000225333, 0.000223], [0.000222409, 0.00022], [0.000221565, 0.000221], [0.00022015, 0.000221], [0.0002, 0.00022]],
    EUR: [[3.813802, 3.81202], [3.81678, 3.82154], [3.895047, 3.968554], [4.0709245, 4.173295], [4.165029, 4.156763], [4.2303, 4.3039], [4.2471585, 4.190438], [4.238618, 4.286798], [4.297358, 4.307918], [4.272450565, 4.238804], [4.245499, 4.252055], [4.2815035, 4.310952]],
    RON: [[0.764548, 0.765998], [0.768002, 0.767791], [0.797524, 0.797001], [0.82849, 0.837801], [0.817803, 0.821203], [0.839046, 0.847233], [0.845074742, 0.825411], [0.8843073143, 0.844758], [0.849537045, 0.84782], [0.839788304, 0.833106], [0.83463165, 0.835438], [0.8453, 0.8463]],
    UZS: [[0.00028, 0.00028], [0.000284, 0.000285], [0.000284, 0.000284], [0.000284, 0.000284], [0.000285, 0.000287], [0.000288, 0.000289], [0.000289, 0.000289], [0.000289, 0.000294], [0.000299, 0.0003], [0.000305, 0.000306], [0.000309, 0.000309], [0.0003, 0.0003]],
    CHF: [[4.040126, 4.035714], [4.062048, 4.071056], [4.156308, 4.161002], [4.406158, 4.461791], [4.430333, 4.459624], [4.511349, 4.606171], [4.59698787, 4.510563], [4.557331476, 4.583749], [4.6113636136, 4.564218], [4.600330478, 4.567218], [4.5704789, 4.563813], [4.613, 4.634]],
    USD: [[3.6725, 3.6725], [3.6725, 3.6725], [3.6725, 3.6725], [3.6725, 3.6725], [3.6725, 3.6725], [3.6725, 3.6725], [3.675, 3.675], [3.675, 3.675], [3.675, 3.675], [3.6725, 3.6725], [3.6725, 3.6725], [3.6725, 3.6725]],
    QAR: [[1.00739, 1.00732], [1.007503, 1.007683], [1.0075725, 1.007462], [1.007531, 1.0076], [1.007655, 1.00771], [1.007697, 1.007683], [1.007586, 1.007489], [1.0074615, 1.007434], [1.0077105, 1.001987], [1.007609565, 1.007517], [1.0076015, 1.007793], [1.0062905, 1.004788]],
    KES: [[0.028395, 0.028425], [0.028413, 0.028425], [0.028402, 0.028403], [0.02836, 0.028381], [0.028416, 0.028425], [0.028421, 0.028414], [0.028424043, 0.028425], [0.028421857, 0.028425], [0.0284275, 0.028436], [0.028411528, 0.028436], [0.028408227, 0.028359], [0.028412655, 0.028469]],
    ZMV: [[0.131807, 0.130988], [0.130331, 0.128432], [0.128172, 0.13081], [0.130545, 0.131867], [0.136213, 0.137934], [0.150304, 0.153693], [0.156938522, 0.161251], [0.158310857, 0.155614], [0.1547868318, 0.153436], [0.161696435, 0.165279], [0.161952, 0.160196], [0.1612, 0.1656]],
    OMF: [[null, null], [null, null], [null, null], [null, null], [null, null], [null, null], [9.538972, 9.538466], [9.538772, 9.538961], [9.538669, 9.538466], [9.538788957, 9.538218], [9.53878765, 9.538713], [9.5385, 9.5382]],
  };

  // Skills state
  const [skillConfigs, setSkillConfigs] = useState(defaultSkillConfigs);

  // SSO state
  const [ssoProviders, setSsoProviders] = useState<SSOProvider[]>([]);
  const [ssoLoading, setSsoLoading] = useState(false);

  // MFA state
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetup, setMfaSetup] = useState<MFASetup | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaVerifying, setMfaVerifying] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);

  // AI / LLM configuration state
  const [aiProvider, setAiProvider] = useState('litellm_proxy');
  const [aiBaseUrl, setAiBaseUrl] = useState('');
  const [aiApiKey, setAiApiKey] = useState('');
  const [aiApiKeyMasked, setAiApiKeyMasked] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [aiTemperature, setAiTemperature] = useState(0.3);
  const [aiMaxTokens, setAiMaxTokens] = useState(1024);
  const [aiConfigured, setAiConfigured] = useState(false);
  const [aiTesting, setAiTesting] = useState(false);
  const [aiTestResult, setAiTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [aiSaving, setAiSaving] = useState(false);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [meUser, usersRes, periodsRes, currenciesRes] = await Promise.all([
        authApi.getMe(),
        usersApi.list(),
        periodsApi.list(),
        currenciesApi.list().catch(() => [] as Currency[]),
      ]);

      // Populate general settings from tenant
      if (meUser.tenant) {
        setTenantName(meUser.tenant.name);
        setReportingCurrency(meUser.tenant.reporting_currency);
        setFyEndMonth(meUser.tenant.fiscal_year_end_month);
        setFyEndDay(meUser.tenant.fiscal_year_end_day);
        setTenantSlug(meUser.tenant.slug);
      }

      setUsers(usersRes);
      setPeriods(periodsRes);
      setCurrencies(currenciesRes);

      // Load AI configuration (non-blocking)
      try {
        const aiCfg = await aiConfigApi.get();
        if (aiCfg.provider) {
          setAiProvider(aiCfg.provider);
          setAiBaseUrl(aiCfg.base_url || '');
          setAiApiKeyMasked(aiCfg.api_key_masked || '');
          setAiModel(aiCfg.model || '');
          setAiTemperature(aiCfg.temperature ?? 0.3);
          setAiMaxTokens(aiCfg.max_tokens ?? 1024);
          setAiConfigured(aiCfg.is_configured ?? false);
        }
      } catch {
        // AI config not yet set — this is fine
      }

      // Auto-select Jan 2025 for FX filter to showcase the loaded data
      const defaultPeriod = periodsRes.find((p) => p.year === 2025 && p.month === 1)
        || periodsRes.find((p) => p.status === PeriodStatus.OPEN)
        || periodsRes[0];
      if (defaultPeriod) {
        setFxPeriodFilter(defaultPeriod.id);
      }
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load FX rates when period filter changes
  useEffect(() => {
    if (!fxPeriodFilter) return;
    const loadFx = async () => {
      try {
        const res = await fxRatesApi.list({ period_id: fxPeriodFilter });
        if (res.length === 0) {
          // Clear first to prevent showing previous period's data
          setFxRates([]);
          // If empty, check if it's a 2025 month and auto-seed
          await autoSeedRates(fxPeriodFilter);
        } else {
          setFxRates(res);
        }
      } catch {
        setFxRates([]);
      }
    };
    loadFx();
  }, [fxPeriodFilter]);

  // Load currencies
  const loadCurrencies = useCallback(async () => {
    setCurrenciesLoading(true);
    try {
      const res = await currenciesApi.list();
      setCurrencies(res);
    } catch {
      setCurrencies([]);
    } finally {
      setCurrenciesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'currencies') {
      loadCurrencies();
    }
  }, [activeTab, loadCurrencies]);

  const handleAddCurrency = async () => {
    if (!newCurrency.code || !newCurrency.name) return;
    setSavingCurrency(true);
    try {
      const created = await currenciesApi.create({ code: newCurrency.code.toUpperCase(), name: newCurrency.name, symbol: newCurrency.symbol });
      setCurrencies((prev) => [...prev, created].sort((a, b) => a.code.localeCompare(b.code)));
      setNewCurrency({ code: '', name: '', symbol: '' });
      setShowAddCurrency(false);
      addNotification({ type: 'success', title: 'Currency Added', message: `${created.code} — ${created.name} has been added.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Add Failed', message: apiErr.detail || 'Failed to add currency.' });
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleToggleCurrency = async (currency: Currency) => {
    try {
      const updated = await currenciesApi.update(currency.id, { is_active: !currency.is_active });
      setCurrencies((prev) => prev.map((c) => (c.id === currency.id ? updated : c)));
    } catch {
      addNotification({ type: 'error', title: 'Update Failed', message: 'Failed to update currency.' });
    }
  };

  const handleDeleteCurrency = async (id: string) => {
    try {
      await currenciesApi.delete(id);
      setCurrencies((prev) => prev.filter((c) => c.id !== id));
      addNotification({ type: 'success', title: 'Deleted', message: 'Currency removed.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete currency.' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // General settings save could go to a tenant update endpoint
      addNotification({ type: 'success', title: 'Settings Saved', message: 'Your settings have been saved.' });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      addNotification({ type: 'error', title: 'Save Failed', message: 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  const handlePeriodStatusChange = async (periodId: string, newStatus: PeriodStatus) => {
    try {
      await periodsApi.updateStatus(periodId, newStatus);
      setPeriods((prev) =>
        prev.map((p) => (p.id === periodId ? { ...p, status: newStatus } : p)),
      );
      addNotification({ type: 'success', title: 'Period Updated', message: `Period status changed to ${newStatus}.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Update Failed', message: apiErr.detail || 'Failed to update period.' });
    }
  };

  const handleCreatePeriod = async () => {
    setSavingPeriod(true);
    try {
      const created = await periodsApi.create({
        year: periodForm.year,
        month: periodForm.month,
        period_type: periodForm.period_type,
        start_date: periodForm.start_date,
        end_date: periodForm.end_date,
      });
      setPeriods((prev) => [...prev, created]);
      setShowPeriodModal(false);
      setPeriodForm({ year: new Date().getFullYear(), month: 12, period_type: PeriodType.ANNUAL as PeriodType, start_date: '', end_date: '' });
      addNotification({ type: 'success', title: 'Period Created', message: `Period ${periodDisplayName(created)} created.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Create Failed', message: apiErr.detail || 'Failed to create period.' });
    } finally {
      setSavingPeriod(false);
    }
  };

  const handleSaveUser = async () => {
    setSavingUser(true);
    try {
      if (editingUser) {
        const updated = await usersApi.update(editingUser.id, {
          email: userForm.email,
          full_name: userForm.full_name,
          role: userForm.role,
        });
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        addNotification({ type: 'success', title: 'User Updated', message: `${updated.full_name} has been updated.` });
      } else {
        const created = await usersApi.create({
          email: userForm.email,
          full_name: userForm.full_name,
          password: userForm.password,
          role: userForm.role,
        });
        setUsers((prev) => [...prev, created]);
        addNotification({ type: 'success', title: 'User Created', message: `${created.full_name} has been invited.` });
      }
      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({ email: '', full_name: '', password: '', role: 'accountant' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Save Failed', message: apiErr.detail || 'Failed to save user.' });
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(userId);
      setUsers((prev) => prev.filter((u) => u.id !== userId));
      addNotification({ type: 'success', title: 'User Deleted', message: 'User has been removed.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete user.' });
    }
  };

  const handleAddFxRate = async () => {
    setSavingFx(true);
    try {
      const created = await fxRatesApi.create({
        period_id: fxPeriodFilter,
        from_currency: newFx.from_currency,
        to_currency: newFx.to_currency,
        rate_type: newFx.rate_type,
        rate: parseFloat(newFx.rate) || 0,
      });
      setFxRates((prev) => [...prev, created]);
      setShowAddFx(false);
      setNewFx({ from_currency: '', to_currency: 'AED', rate_type: RateType.CLOSING, rate: '' });
      addNotification({ type: 'success', title: 'FX Rate Added', message: `${created.from_currency}/${created.to_currency} rate added.` });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Add Failed', message: apiErr.detail || 'Failed to add FX rate.' });
    } finally {
      setSavingFx(false);
    }
  };
  const autoSeedRates = async (periodId: string) => {
    const period = periods.find((p) => String(p.id) === String(periodId));
    if (!period || period.year !== 2025) return;

    const monthIdx = (period.month || 1) - 1; // 0-indexed

    try {
      const payload: Partial<FxRate>[] = [];
      for (const [currency, months] of Object.entries(MONTHLY_RATES)) {
        const ratesForMonth = months[monthIdx];
        if (!ratesForMonth) continue;

        const [avgRate, endRate] = ratesForMonth;

        if (avgRate !== null) {
          payload.push({
            from_currency: currency,
            to_currency: 'AED',
            rate_type: RateType.AVERAGE,
            period_id: String(periodId),
            rate: Number(avgRate.toFixed(6)),
          });
        }

        if (endRate !== null) {
          payload.push({
            from_currency: currency,
            to_currency: 'AED',
            rate_type: RateType.CLOSING,
            period_id: String(periodId),
            rate: Number(endRate.toFixed(6)),
          });
        }
      }

      if (payload.length > 0) {
        await fxRatesApi.bulkCreate(payload);

        // Fetch the newly seeded rates to display them immediately
        const seededRes = await fxRatesApi.list({ period_id: periodId });
        setFxRates(seededRes);
      }
    } catch (err) {
      console.error('Failed to auto-seed 2025 FX rates:', err);
    }
  };

  const handleDeleteFxRate = async (id: string) => {
    try {
      await fxRatesApi.delete(id);
      setFxRates((prev) => prev.filter((r) => r.id !== id));
      addNotification({ type: 'success', title: 'FX Rate Deleted', message: 'FX rate has been removed.' });
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete FX rate.' });
    }
  };

  const displayRates = isReverseView
    ? fxRates
      .filter((r) => Number(r.rate) !== 0)
      .map((r) => ({
        ...r,
        id: `rev-${r.id}`,
        from_currency: r.to_currency,
        to_currency: r.from_currency,
        rate: 1 / Number(r.rate),
      }))
    : fxRates;

  const tabs: { id: SettingsTab; label: string; icon: React.ElementType }[] = [
    { id: 'general', label: 'General', icon: Building },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'periods', label: 'Periods', icon: Calendar },
    { id: 'currencies', label: 'Currencies', icon: Coins },
    { id: 'fx_rates', label: 'FX Rates', icon: DollarSign },
    { id: 'skills', label: 'Skills', icon: Zap },
    { id: 'ai_llm', label: 'AI / LLM', icon: Brain },
    { id: 'sso', label: 'SSO', icon: Globe },
    { id: 'mfa', label: 'MFA', icon: Key },
  ];

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="skeleton w-48 h-7" />
        <div className="card p-6 animate-pulse">
          <div className="space-y-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="skeleton w-full h-10" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        </div>
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-300 mb-3" />
          <p className="text-sm text-red-600">{error}</p>
          <button onClick={loadData} className="btn-primary mt-4">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your consolidation platform configuration</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="btn-primary">
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : saved ? (
            <>
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Saved
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700',
              )}
            >
              <TabIcon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'general' && (
        <div className="card p-6 max-w-2xl">
          <h2 className="text-base font-semibold text-gray-900 mb-6">General Settings</h2>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Organization Name</label>
              <input
                className="input-field"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Reporting Currency</label>
              <select
                className="select-field w-48"
                value={reportingCurrency}
                onChange={(e) => setReportingCurrency(e.target.value)}
              >
                {(currencies.length > 0
                  ? currencies.filter((c) => c.is_active).map((c) => c.code)
                  : ['USD', 'AED', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'KES']
                ).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">All consolidated amounts will be reported in this currency</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Fiscal Year End Month</label>
                <select
                  className="select-field"
                  value={fyEndMonth}
                  onChange={(e) => setFyEndMonth(parseInt(e.target.value))}
                >
                  {months.map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Fiscal Year End Day</label>
                <input
                  type="number"
                  className="input-field"
                  value={fyEndDay}
                  onChange={(e) => setFyEndDay(parseInt(e.target.value))}
                  min={1}
                  max={31}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Tenant Slug</label>
              <input
                className="input-field bg-gray-50"
                value={tenantSlug}
                readOnly
                disabled
              />
              <p className="text-xs text-gray-400 mt-1">Used for URL identification. Cannot be changed.</p>
            </div>
            <p className="text-xs text-gray-500 pt-2 border-t border-gray-100">
              All active entities are treated as intercompany participants automatically.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">
              Team Members <span className="text-gray-400 font-normal ml-1">({users.length})</span>
            </h2>
            <button
              onClick={() => {
                setEditingUser(null);
                setUserForm({ email: '', full_name: '', password: '', role: 'accountant' });
                setShowUserModal(true);
              }}
              className="btn-primary text-sm py-1.5"
            >
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Invite User
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Name</th>
                  <th className="table-header">Email</th>
                  <th className="table-header">Role</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Joined</th>
                  <th className="table-header w-20"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="table-cell">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-[10px] font-bold">
                          {u.full_name.split(' ').map((n) => n[0]).join('').toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-900">{u.full_name}</span>
                      </div>
                    </td>
                    <td className="table-cell text-gray-500 font-mono text-xs">{u.email}</td>
                    <td className="table-cell">
                      <RoleBadge role={u.role} />
                    </td>
                    <td className="table-cell">
                      {u.is_active ? (
                        <span className="badge bg-green-50 text-green-700">Active</span>
                      ) : (
                        <span className="badge bg-gray-100 text-gray-500">Inactive</span>
                      )}
                    </td>
                    <td className="table-cell text-gray-500 text-xs">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setEditingUser(u);
                            setUserForm({ email: u.email, full_name: u.full_name, password: '', role: u.role });
                            setShowUserModal(true);
                          }}
                          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(u.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'periods' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Period Management</h2>
            <button onClick={() => setShowPeriodModal(true)} className="btn-primary text-sm py-1.5">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Create Period
            </button>
          </div>
          <div className="card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Period</th>
                  <th className="table-header">Start Date</th>
                  <th className="table-header">End Date</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {periods.map((period) => (
                  <tr key={period.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="table-cell font-medium text-gray-900">{periodDisplayName(period)}</td>
                    <td className="table-cell text-gray-500 text-xs">{period.start_date}</td>
                    <td className="table-cell text-gray-500 text-xs">{period.end_date}</td>
                    <td className="table-cell">
                      <PeriodStatusBadge status={period.status} />
                    </td>
                    <td className="table-cell">
                      <div className="flex items-center gap-1">
                        {period.status === PeriodStatus.OPEN && (
                          <button
                            onClick={() => handlePeriodStatusChange(period.id, PeriodStatus.CLOSED)}
                            className="text-xs text-amber-600 hover:text-amber-700 font-medium px-2 py-1 rounded hover:bg-amber-50 transition-colors"
                          >
                            Close
                          </button>
                        )}
                        {period.status === PeriodStatus.CLOSED && (
                          <>
                            <button
                              onClick={() => handlePeriodStatusChange(period.id, PeriodStatus.OPEN)}
                              className="text-xs text-green-600 hover:text-green-700 font-medium px-2 py-1 rounded hover:bg-green-50 transition-colors"
                            >
                              Reopen
                            </button>
                            <button
                              onClick={() => handlePeriodStatusChange(period.id, PeriodStatus.LOCKED)}
                              className="text-xs text-gray-600 hover:text-gray-700 font-medium px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                            >
                              Lock
                            </button>
                          </>
                        )}
                        {period.status === PeriodStatus.LOCKED && (
                          <span className="text-xs text-gray-400 italic">Locked</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== CURRENCIES TAB ==================== */}
      {activeTab === 'currencies' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Currency Registry</h2>
            <button onClick={() => setShowAddCurrency(true)} className="btn-primary text-sm py-1.5">
              <Plus className="w-3.5 h-3.5 mr-1.5" />
              Add Currency
            </button>
          </div>

          {/* Add Currency form */}
          {showAddCurrency && (
            <div className="card p-4">
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Code (ISO 4217)</label>
                  <input
                    className="input-field text-sm w-24 uppercase"
                    maxLength={3}
                    placeholder="USD"
                    value={newCurrency.code}
                    onChange={(e) => setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase() })}
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                  <input
                    className="input-field text-sm"
                    placeholder="US Dollar"
                    value={newCurrency.name}
                    onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Symbol</label>
                  <input
                    className="input-field text-sm w-20"
                    placeholder="$"
                    value={newCurrency.symbol}
                    onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                  />
                </div>
                <button
                  onClick={handleAddCurrency}
                  disabled={!newCurrency.code || newCurrency.code.length !== 3 || !newCurrency.name || savingCurrency}
                  className="btn-primary text-sm py-2"
                >
                  {savingCurrency ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                </button>
                <button onClick={() => { setShowAddCurrency(false); setNewCurrency({ code: '', name: '', symbol: '' }); }} className="btn-secondary text-sm py-2">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          <div className="card overflow-hidden">
            {currenciesLoading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 animate-pulse">
                    <div className="skeleton w-16 h-4" />
                    <div className="skeleton flex-1 h-4" />
                    <div className="skeleton w-12 h-4" />
                    <div className="skeleton w-16 h-4" />
                  </div>
                ))}
              </div>
            ) : currencies.length === 0 ? (
              <div className="py-12 text-center">
                <Coins className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No currencies configured yet</p>
                <p className="text-xs text-gray-400 mt-1">Add currencies to use across entities, FX rates, and reports</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="table-header w-24">Code</th>
                    <th className="table-header">Name</th>
                    <th className="table-header w-20">Symbol</th>
                    <th className="table-header w-24 text-center">Status</th>
                    <th className="table-header w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currencies.map((cur) => (
                    <tr key={cur.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="table-cell">
                        <span className="badge bg-gray-100 text-gray-700 font-mono">{cur.code}</span>
                      </td>
                      <td className="table-cell text-gray-700">{cur.name}</td>
                      <td className="table-cell text-center text-gray-500 font-mono">{cur.symbol || '—'}</td>
                      <td className="table-cell text-center">
                        <button
                          onClick={() => handleToggleCurrency(cur)}
                          className={clsx(
                            'inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-0.5 transition-colors',
                            cur.is_active
                              ? 'bg-green-50 text-green-700 hover:bg-green-100'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200',
                          )}
                        >
                          {cur.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="table-cell">
                        <button
                          onClick={() => handleDeleteCurrency(cur.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'fx_rates' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-900">FX Rates</h2>
              <select
                className="select-field text-sm w-36"
                value={fxPeriodFilter}
                onChange={(e) => setFxPeriodFilter(e.target.value)}
              >
                {periods.map((p) => (
                  <option key={p.id} value={p.id}>{periodDisplayName(p)}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsReverseView(!isReverseView)}
                className={clsx(
                  "btn-secondary text-sm py-1.5 px-3",
                  isReverseView && "bg-primary-50 text-primary-700 border-primary-200"
                )}
                title={isReverseView ? 'Showing Reverse Rates (AED → Currency)' : 'Showing Original Rates (Currency → AED)'}
              >
                <div className="flex items-center gap-1.5">
                  <Globe className={clsx("w-3.5 h-3.5", isReverseView ? "text-primary-600" : "text-gray-400")} />
                  {isReverseView ? 'Show Original Rates' : 'Show Reverse Rates'}
                </div>
              </button>

              <button onClick={() => setShowAddFx(true)} className="btn-primary text-sm py-1.5">
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add Rate
              </button>
            </div>
          </div>

          {/* Add FX Rate form */}
          {showAddFx && (
            <div className="card p-4">
              <div className="flex items-end gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">From Currency</label>
                  <select className="select-field text-sm w-28" value={newFx.from_currency} onChange={(e) => setNewFx({ ...newFx, from_currency: e.target.value })}>
                    <option value="">Select</option>
                    {(currencies.length > 0
                      ? currencies.filter((c) => c.is_active).map((c) => c.code)
                      : ['AED', 'GBP', 'AZN', 'INR', 'Rp', 'EUR', 'RON', 'UZS', 'CHF',
                        'USD', 'QAR', 'KES', 'ZMV', 'OMF',
                        'SGD', 'JPY', 'SEK', 'CAD', 'AUD', 'CNY', 'HKD', 'SAR', 'BHD', 'KWD']
                    ).map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">To Currency (AED base)</label>
                  <input className="input-field text-sm w-24 bg-gray-50" value={newFx.to_currency} readOnly />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Rate Type</label>
                  <select className="select-field text-sm w-28" value={newFx.rate_type} onChange={(e) => setNewFx({ ...newFx, rate_type: e.target.value as RateType })}>
                    <option value={RateType.CLOSING}>Closing</option>
                    <option value={RateType.AVERAGE}>Average</option>
                    <option value={RateType.HISTORICAL}>Historical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Rate</label>
                  <input
                    type="number"
                    step="0.0001"
                    className="input-field text-sm w-32"
                    value={newFx.rate}
                    onChange={(e) => setNewFx({ ...newFx, rate: e.target.value })}
                    placeholder="0.0000"
                  />
                </div>
                <button onClick={handleAddFxRate} disabled={!newFx.from_currency || !newFx.rate || savingFx} className="btn-primary text-sm py-2">
                  {savingFx ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Add'}
                </button>
                <button onClick={() => setShowAddFx(false)} className="btn-secondary text-sm py-2">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}

          <div className="card overflow-hidden">
            {displayRates.length === 0 ? (
              <div className="py-12 text-center">
                <DollarSign className="w-10 h-10 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No FX rates for this period</p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="table-header">From</th>
                    <th className="table-header">To</th>
                    <th className="table-header">Rate Type</th>
                    <th className="table-header text-right">Rate</th>
                    <th className="table-header w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {displayRates.map((rate) => (
                    <tr key={rate.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="table-cell">
                        <span className="badge bg-gray-100 text-gray-700 font-mono">{rate.from_currency}</span>
                      </td>
                      <td className="table-cell">
                        <span className="badge bg-primary-50 text-primary-700 font-mono">{rate.to_currency}</span>
                      </td>
                      <td className="table-cell capitalize text-gray-600">{rate.rate_type}</td>
                      <td className="table-cell text-right font-mono text-sm font-medium text-gray-900">
                        {Number(rate.rate).toFixed(6)}
                      </td>
                      <td className="table-cell">
                        {!isReverseView && (
                          <button
                            onClick={() => handleDeleteFxRate(rate.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'skills' && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-900">Consolidation Skills Configuration</h2>
          <div className="space-y-3">
            {skillConfigs.map((skill, index) => (
              <div key={skill.name} className="card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                      <Zap className="w-4 h-4 text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{skill.display_name}</h3>
                      <p className="text-xs text-gray-500">Step {index + 1} in the consolidation pipeline</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={skill.enabled}
                      onChange={(e) => {
                        setSkillConfigs((prev) =>
                          prev.map((s) =>
                            s.name === skill.name ? { ...s, enabled: e.target.checked } : s,
                          ),
                        );
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-500 mb-2 uppercase">Parameters</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(skill.params).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="text-xs font-mono font-medium text-gray-700">
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'ai_llm' && (
        <div className="card p-6 max-w-2xl">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">AI / LLM Configuration</h2>
              <p className="text-sm text-gray-500">Configure the AI model provider for error analysis, suggestions, and intelligent features.</p>
            </div>
          </div>

          <div className="space-y-5 mt-6">
            {/* Provider */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Provider</label>
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                value={aiProvider}
                onChange={(e) => setAiProvider(e.target.value)}
              >
                <option value="litellm_proxy">LiteLLM Proxy</option>
                <option value="openai">OpenAI Direct</option>
                <option value="anthropic">Anthropic (Claude) Direct</option>
                <option value="gemini">Google Gemini Direct</option>
              </select>
            </div>

            {/* Base URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Base URL</label>
              <input
                type="text"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                value={aiBaseUrl}
                onChange={(e) => setAiBaseUrl(e.target.value)}
                placeholder="https://model.iamsaif.ai"
              />
              <p className="text-xs text-gray-400 mt-1">
                {aiProvider === 'litellm_proxy' && 'Your LiteLLM proxy endpoint'}
                {aiProvider === 'openai' && 'Default: https://api.openai.com/v1'}
                {aiProvider === 'anthropic' && 'Default: https://api.anthropic.com'}
                {aiProvider === 'gemini' && 'Default: https://generativelanguage.googleapis.com/v1beta/openai'}
              </p>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">API Key</label>
              <input
                type="password"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                value={aiApiKey}
                onChange={(e) => setAiApiKey(e.target.value)}
                placeholder={aiApiKeyMasked || 'Enter API key'}
              />
              {aiApiKeyMasked && !aiApiKey && (
                <p className="text-xs text-gray-500 mt-1">Saved key: {aiApiKeyMasked}</p>
              )}
            </div>

            {/* Model */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Model Name</label>
              <input
                type="text"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                value={aiModel}
                onChange={(e) => setAiModel(e.target.value)}
                placeholder="gpt-4o"
              />
            </div>

            {/* Temperature & Max Tokens */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Temperature</label>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.1"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  value={aiTemperature}
                  onChange={(e) => setAiTemperature(parseFloat(e.target.value) || 0)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Max Tokens</label>
                <input
                  type="number"
                  min="100"
                  max="4096"
                  step="100"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                  value={aiMaxTokens}
                  onChange={(e) => setAiMaxTokens(parseInt(e.target.value) || 1024)}
                />
              </div>
            </div>

            {/* Test + Save Buttons */}
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={async () => {
                  setAiTesting(true);
                  setAiTestResult(null);
                  try {
                    const result = await aiConfigApi.test({
                      provider: aiProvider,
                      base_url: aiBaseUrl,
                      api_key: aiApiKey || '__existing__',
                      model: aiModel,
                    });
                    setAiTestResult(result);
                  } catch {
                    setAiTestResult({ success: false, message: 'Failed to test connection.' });
                  } finally {
                    setAiTesting(false);
                  }
                }}
                disabled={aiTesting || !aiBaseUrl || !aiModel}
                className="btn-secondary text-sm"
              >
                {aiTesting ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Testing...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-1.5" /> Test Connection</>
                )}
              </button>
              <button
                onClick={async () => {
                  setAiSaving(true);
                  try {
                    await aiConfigApi.save({
                      provider: aiProvider,
                      base_url: aiBaseUrl,
                      api_key: aiApiKey || '__existing__',
                      model: aiModel,
                      temperature: aiTemperature,
                      max_tokens: aiMaxTokens,
                    });
                    setAiConfigured(true);
                    if (aiApiKey) {
                      setAiApiKeyMasked(aiApiKey.length > 8 ? aiApiKey.slice(0, 4) + '...' + aiApiKey.slice(-4) : '****');
                    }
                    setAiApiKey('');
                    addNotification({ type: 'success', title: 'AI Config Saved', message: 'LLM configuration has been saved.' });
                  } catch {
                    addNotification({ type: 'error', title: 'Save Failed', message: 'Failed to save AI configuration.' });
                  } finally {
                    setAiSaving(false);
                  }
                }}
                disabled={aiSaving || !aiBaseUrl || !aiModel}
                className="btn-primary text-sm"
              >
                {aiSaving ? (
                  <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-1.5" /> Save Configuration</>
                )}
              </button>
            </div>

            {/* Test Result */}
            {aiTestResult && (
              <div className={clsx(
                'px-4 py-3 rounded-lg border',
                aiTestResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
              )}>
                <div className="flex items-center gap-2">
                  {aiTestResult.success ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  )}
                  <p className={clsx('text-sm', aiTestResult.success ? 'text-green-800' : 'text-red-800')}>
                    {aiTestResult.message}
                  </p>
                </div>
              </div>
            )}

            {/* Status Indicator */}
            {aiConfigured && !aiTestResult && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="w-4 h-4" />
                AI is configured and active
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'sso' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Single Sign-On (SSO)</h2>
              <p className="text-xs text-gray-500 mt-0.5">Configure SAML or OAuth providers for enterprise authentication</p>
            </div>
            <button
              onClick={async () => {
                setSsoLoading(true);
                try {
                  const providers = await ssoApi.providers();
                  setSsoProviders(providers);
                } catch {
                  setSsoProviders([]);
                } finally {
                  setSsoLoading(false);
                }
              }}
              disabled={ssoLoading}
              className="btn-secondary text-sm"
            >
              {ssoLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Globe className="w-4 h-4 mr-1.5" />}
              Refresh Providers
            </button>
          </div>
          {ssoProviders.length === 0 ? (
            <div className="card py-12 text-center">
              <Globe className="w-10 h-10 mx-auto text-gray-300 mb-2" />
              <h3 className="text-base font-medium text-gray-900">No SSO Providers Configured</h3>
              <p className="text-sm text-gray-500 mt-1">
                Click "Refresh Providers" to load available SSO providers, or contact your administrator.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {ssoProviders.map((provider) => (
                <div key={provider.id} className="card p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      provider.enabled ? 'bg-green-50' : 'bg-gray-100',
                    )}>
                      <Globe className={clsx('w-5 h-5', provider.enabled ? 'text-green-600' : 'text-gray-400')} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{provider.provider_name}</h3>
                      <p className="text-xs text-gray-500 capitalize">{provider.provider_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={clsx(
                      'badge',
                      provider.enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500',
                    )}>
                      {provider.enabled ? 'Active' : 'Inactive'}
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={provider.enabled}
                        onChange={async (e) => {
                          try {
                            await ssoApi.configure(provider.id, { enabled: e.target.checked });
                            setSsoProviders((prev) =>
                              prev.map((p) => (p.id === provider.id ? { ...p, enabled: e.target.checked } : p)),
                            );
                            addNotification({
                              type: 'success',
                              title: 'SSO Updated',
                              message: `${provider.provider_name} has been ${e.target.checked ? 'enabled' : 'disabled'}.`,
                            });
                          } catch {
                            addNotification({ type: 'error', title: 'Update Failed', message: 'Failed to update SSO provider.' });
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'mfa' && (
        <div className="space-y-4 max-w-2xl">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Multi-Factor Authentication (MFA)</h2>
            <p className="text-xs text-gray-500 mt-0.5">Secure your account with TOTP-based two-factor authentication</p>
          </div>

          {/* MFA Status */}
          <div className={clsx(
            'card p-5 border-l-4',
            mfaEnabled ? 'border-l-green-500' : 'border-l-amber-500',
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {mfaEnabled ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                )}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    MFA is {mfaEnabled ? 'Enabled' : 'Disabled'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {mfaEnabled
                      ? 'Your account is protected with two-factor authentication.'
                      : 'Enable MFA to add an extra layer of security.'}
                  </p>
                </div>
              </div>
              {!mfaEnabled ? (
                <button
                  onClick={async () => {
                    setMfaLoading(true);
                    try {
                      const setup = await mfaApi.setup();
                      setMfaSetup(setup);
                    } catch {
                      addNotification({ type: 'error', title: 'MFA Setup Failed', message: 'Failed to initialize MFA setup.' });
                    } finally {
                      setMfaLoading(false);
                    }
                  }}
                  disabled={mfaLoading}
                  className="btn-primary text-sm"
                >
                  {mfaLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Key className="w-4 h-4 mr-1.5" />}
                  Setup MFA
                </button>
              ) : (
                <button
                  onClick={async () => {
                    const code = prompt('Enter your MFA code to disable:');
                    if (!code) return;
                    try {
                      await mfaApi.disable(code);
                      setMfaEnabled(false);
                      setMfaSetup(null);
                      addNotification({ type: 'success', title: 'MFA Disabled', message: 'Two-factor authentication has been disabled.' });
                    } catch {
                      addNotification({ type: 'error', title: 'Disable Failed', message: 'Invalid code. MFA was not disabled.' });
                    }
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                >
                  Disable MFA
                </button>
              )}
            </div>
          </div>

          {/* MFA Setup - QR Code */}
          {mfaSetup && !mfaEnabled && (
            <div className="card p-6 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Setup TOTP Authentication</h3>
              <div className="flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-48 h-48 bg-gray-100 rounded-lg flex items-center justify-center border border-gray-200">
                    {mfaSetup.qr_code_uri ? (
                      <img
                        src={mfaSetup.qr_code_uri}
                        alt="MFA QR Code"
                        className="w-44 h-44"
                      />
                    ) : (
                      <Key className="w-12 h-12 text-gray-300" />
                    )}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-2 text-center">Scan with Google Authenticator</p>
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Secret Key (Manual Entry)</label>
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono bg-gray-50 px-3 py-2 rounded border border-gray-200 break-all">
                        {mfaSetup.secret}
                      </code>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Verification Code</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        className="input-field text-sm w-36 font-mono tracking-widest"
                        maxLength={6}
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                      />
                      <button
                        onClick={async () => {
                          setMfaVerifying(true);
                          try {
                            const result = await mfaApi.verify(mfaCode);
                            if (result.verified) {
                              setMfaEnabled(true);
                              setShowBackupCodes(true);
                              addNotification({ type: 'success', title: 'MFA Enabled', message: 'Two-factor authentication is now active.' });
                            } else {
                              addNotification({ type: 'error', title: 'Verification Failed', message: 'Invalid code. Please try again.' });
                            }
                          } catch {
                            addNotification({ type: 'error', title: 'Verification Failed', message: 'Failed to verify code.' });
                          } finally {
                            setMfaVerifying(false);
                          }
                        }}
                        disabled={mfaCode.length !== 6 || mfaVerifying}
                        className="btn-primary text-sm"
                      >
                        {mfaVerifying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                        Verify & Enable
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Backup Codes */}
          {showBackupCodes && mfaSetup?.backup_codes && mfaSetup.backup_codes.length > 0 && (
            <div className="card p-6 border-l-4 border-l-amber-500">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <h3 className="text-sm font-semibold text-gray-900">Backup Codes</h3>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Save these backup codes in a safe place. Each code can only be used once.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {mfaSetup.backup_codes.map((code, i) => (
                  <code key={i} className="text-xs font-mono bg-gray-50 px-3 py-2 rounded border border-gray-200 text-center">
                    {code}
                  </code>
                ))}
              </div>
              <button
                onClick={() => setShowBackupCodes(false)}
                className="mt-3 text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                I've saved my codes
              </button>
            </div>
          )}

          {/* Check MFA Status on tab load */}
          {activeTab === 'mfa' && !mfaEnabled && !mfaSetup && (
            <div className="card p-4">
              <button
                onClick={async () => {
                  try {
                    const status = await mfaApi.status();
                    setMfaEnabled(status.enabled);
                  } catch {
                    // silently fail
                  }
                }}
                className="text-xs text-primary-600 hover:text-primary-800 font-medium"
              >
                Check MFA Status
              </button>
            </div>
          )}
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Edit User' : 'Invite New User'}
              </h2>
              <button onClick={() => { setShowUserModal(false); setEditingUser(null); }} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name</label>
                <input
                  className="input-field"
                  value={userForm.full_name}
                  onChange={(e) => setUserForm({ ...userForm, full_name: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                <input
                  className="input-field"
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="john@company.com"
                />
              </div>
              {!editingUser && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <input
                    className="input-field"
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    placeholder="Initial password"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Role</label>
                <select
                  className="select-field"
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value as User['role'] })}
                >
                  <option value="admin">Admin</option>
                  <option value="accountant">Accountant</option>
                  <option value="auditor">Auditor</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => { setShowUserModal(false); setEditingUser(null); }} className="btn-secondary">Cancel</button>
              <button onClick={handleSaveUser} disabled={savingUser || !userForm.email || !userForm.full_name} className="btn-primary">
                {savingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingUser ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Period Modal */}
      {showPeriodModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Create Period</h2>
              <button onClick={() => setShowPeriodModal(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Year</label>
                  <input
                    type="number"
                    className="input-field"
                    value={periodForm.year}
                    onChange={(e) => setPeriodForm({ ...periodForm, year: Number(e.target.value) })}
                    min={2000}
                    max={2100}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Month</label>
                  <select
                    className="input-field"
                    value={periodForm.month}
                    onChange={(e) => setPeriodForm({ ...periodForm, month: Number(e.target.value) })}
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    className="input-field"
                    value={periodForm.period_type}
                    onChange={(e) => setPeriodForm({ ...periodForm, period_type: e.target.value as PeriodType })}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annual">Annual</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Start Date</label>
                  <input
                    type="date"
                    className="input-field"
                    value={periodForm.start_date}
                    onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">End Date</label>
                  <input
                    type="date"
                    className="input-field"
                    value={periodForm.end_date}
                    onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowPeriodModal(false)} className="btn-secondary">Cancel</button>
              <button
                onClick={handleCreatePeriod}
                disabled={savingPeriod || !periodForm.start_date || !periodForm.end_date}
                className="btn-primary"
              >
                {savingPeriod ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
